import { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { NavLink, useNavigate } from "react-router-dom";
import Wordmark from "./Wordmark";
import { useAuth } from "../context/AuthContext";
import { tokens } from "../theme";
import { INSTAGRAM_URL } from "../auth";

const MenuIcon = () => <Box component="span">☰</Box>;
const PersonOutline = () => <Box component="span">◌</Box>;

const LINKS = [
  { to: "/works", label: "Works" },
  { to: "/collections", label: "Collections" },
  { to: "/about", label: "About" },
];

function navLinkSx(isActive: boolean) {
  return {
    position: "relative" as const,
    textDecoration: "none",
    color: isActive ? tokens.ink : tokens.taupe,
    fontFamily: tokens.sans,
    fontSize: "0.74rem",
    fontWeight: 600,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    paddingBottom: "4px",
    transition: "color 160ms ease",
    "&:hover": { color: tokens.ink },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      bottom: 0,
      height: "1px",
      width: isActive ? "100%" : "0%",
      background: tokens.brass,
      transition: "width 220ms ease",
    },
    "&:hover::after": { width: "100%" },
  };
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [drawer, setDrawer] = useState(false);

  const handleLogout = () => {
    setAnchor(null);
    logout();
    navigate("/");
  };

  return (
    <AppBar
      position="sticky"
      color="transparent"
      sx={{
        backdropFilter: "saturate(180%) blur(8px)",
        backgroundColor: "rgba(250,247,241,0.82)",
        borderBottom: `1px solid ${tokens.line}`,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 64, md: 76 }, px: { xs: 2.5, md: 5 } }}>
        <Wordmark />
        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 4,
          }}
        >
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              style={({ isActive }) =>
                navLinkSx(isActive) as React.CSSProperties
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Box
            component="a"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={navLinkSx(false)}
          >
            Instagram
          </Box>
          {user ? (
            <>
              <Box sx={{ width: "1px", height: 22, bgcolor: tokens.line }} />
              <IconButton
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ color: tokens.ink }}
              >
                <PersonOutline fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{ display: "block", lineHeight: 1 }}
                  >
                    Signed in
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: tokens.display,
                      fontSize: "1.2rem",
                      color: tokens.ink,
                    }}
                  >
                    {user.name}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setAnchor(null);
                    navigate("/admin");
                  }}
                >
                  Studio / Admin
                </MenuItem>
                <MenuItem onClick={handleLogout}>Sign out</MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              component={NavLink}
              to="/login"
              variant="outlined"
              size="small"
              sx={{
                letterSpacing: "0.14em",
                fontSize: "0.7rem",
                textTransform: "uppercase",
              }}
            >
              Sign in
            </Button>
          )}
        </Box>

        <Box sx={{ display: { xs: "flex", md: "none" } }}>
          <IconButton
            edge="end"
            onClick={() => setDrawer(true)}
            sx={{ color: tokens.ink }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>

      <Drawer
        anchor="right"
        open={drawer}
        onClose={() => setDrawer(false)}
        PaperProps={{ sx: { width: 280, bgcolor: tokens.bone, px: 1, py: 2 } }}
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <Wordmark />
        </Box>
        <Divider />
        <List>
          {LINKS.map((l) => (
            <ListItemButton
              key={l.to}
              component={NavLink}
              to={l.to}
              onClick={() => setDrawer(false)}
              sx={{ py: 1.5 }}
            >
              <Typography
                sx={{
                  fontFamily: tokens.display,
                  fontSize: "1.5rem",
                  color: tokens.ink,
                }}
              >
                {l.label}
              </Typography>
            </ListItemButton>
          ))}
          <ListItemButton
            component="a"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ py: 1.5 }}
          >
            <Typography
              sx={{
                fontFamily: tokens.display,
                fontSize: "1.5rem",
                color: tokens.ink,
              }}
            >
              Instagram
            </Typography>
          </ListItemButton>
        </List>
        <Divider />
        <List>
          {user ? (
            <>
              <ListItemButton
                component={NavLink}
                to="/admin"
                onClick={() => setDrawer(false)}
              >
                Studio / Admin
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  setDrawer(false);
                  handleLogout();
                }}
              >
                Sign out
              </ListItemButton>
            </>
          ) : (
            <ListItemButton
              component={NavLink}
              to="/login"
              onClick={() => setDrawer(false)}
            >
              Sign in
            </ListItemButton>
          )}
        </List>
      </Drawer>
    </AppBar>
  );
}
